// DS6.9.1
import React, { useMemo, useContext } from 'react';
import cn from 'classnames';
import { PapiBadge } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiEvent } from '@wix/da-bi/pkg/events';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import Badge from '@wix/da-ds/pkg/Badge';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink/PlainButtonOrLink';
import { usePushModal } from '../../utils/hooks/usePushModal';
import { ModalType } from '../../Modals/redux/types';
import CustomNotificationProvider from '../../Notifications/CustomNotificationContext';
import PopoverNotification from '../../Notifications/PopoverNotification';
import { NotificationType } from '../../Notifications';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import CoreBadgeButton from '../CoreBadgeButton';
import { badgeNotificationsFilter } from '../utils';
import { CoreBadgeRecipient, CoreBadgeType } from '../types';

import s from './CoreBadgeList.scss';

const STACKED_BADGES_THRESHOLD = 2;

export interface Props {
  badges: PapiBadge[];
  recipient: CoreBadgeRecipient;
  targetTypeId: number;
  targetItemId: number;
  notificationQueueName: string;
  biData?: BiEvent;
  className?: string;
  size?: 'large' | 'small';
}

const CoreBadgeList: React.FC<React.PropsWithChildren<Props>> = ({
  badges,
  recipient,
  targetTypeId,
  targetItemId,
  notificationQueueName,
  biData,
  className,
  size = 'large',
}) => {
  const isMobile = useContext(MobileContext);
  const { width: screenWidth } = useContext(ScreenSizeContext);
  const pushAwardsModal = usePushModal(ModalType.RECEIVED_AWARDS);

  // tally up the total of badges awarded
  const badgeCount = useMemo(
    () => badges.reduce((prev, curr) => prev + (curr.stackCount ?? 0), 0),
    [badges]
  );

  if (badges.length < 1) {
    return null;
  }

  // only last four badges
  const visibleBadges = badges.slice(-4);

  // if over 1000 badges, represent as 1k
  const totalBadges =
    badgeCount > 999 ? `${Math.floor(badgeCount / 1000)}k` : badgeCount;

  // if "stacked" it means the badges will be displayed in an overlapped manner
  const isStacked = visibleBadges.length > STACKED_BADGES_THRESHOLD;

  return (
    <CustomNotificationProvider queueName={notificationQueueName}>
      <Section
        className={cn(
          s['root'],
          s[`size-${size}`],
          isStacked && s['stacked'],
          className
        )}
      >
        <VisuallyHidden>
          <Heading>Badge Awards</Heading>
        </VisuallyHidden>

        {visibleBadges.map(
          (badge, idx) =>
            badge.stackCount && (
              <PopoverNotification
                key={`CoreBadgeList-${idx}`}
                placement={isMobile || screenWidth <= 1250 ? 'top' : undefined}
                secondaryPlacement={
                  isMobile || screenWidth <= 1250 ? 'end' : undefined
                }
                className={cn(s['button'], idx === 0 && s['button-first'])}
                contentClassName={s['notification']}
                notificationTypes={[NotificationType.Badge]}
                notificationFilter={badgeNotificationsFilter(badge.name)}
              >
                <CoreBadgeButton
                  key={badge.name}
                  type={badge.name as CoreBadgeType}
                  badges={badges}
                  recipient={recipient}
                  targetTypeId={targetTypeId}
                  targetItemId={targetItemId}
                  biData={biData}
                  hideTooltip
                >
                  <Badge
                    badge={badge}
                    className={s['badge']}
                    size={size === 'large' ? 24 : 16}
                  />
                </CoreBadgeButton>
              </PopoverNotification>
            )
        )}

        {totalBadges && (
          <PlainButtonOrLink
            className={s['badge-count']}
            biData={biData}
            onClick={() =>
              pushAwardsModal({
                badges,
                recipient,
                typeId: targetTypeId,
                itemId: targetItemId,
              })
            }
          >
            {totalBadges}
          </PlainButtonOrLink>
        )}
      </Section>
    </CustomNotificationProvider>
  );
};
CoreBadgeList.displayName = 'CoreBadgeList';

export default CoreBadgeList;
